import { wait } from "./wait";

export function staleWhileRevalidate<T>(
  functionToWrap: () => Promise<T> | T,
  freshDurationMs: number,
  totalCacheMs: number
) {
  let cachedData: null | T = null;
  let freshUntil: null | Date = null;
  let cachedUntil: null | Date = null;
  let refreshPromise: Promise<T> | null = null;
  let refreshError: null | unknown = null;
  if (freshDurationMs < 0 || totalCacheMs < 0) {
    throw new Error("Duration values must be non-negative");
  }
  if (freshDurationMs > totalCacheMs) {
    throw new Error(
      "Total cache duration must be greater than or equal to fresh"
    );
  }

  async function refreshCache(now: Date) {
    if (refreshPromise && refreshPromise instanceof Promise) {
      await refreshPromise;
      return;
    }
    try {
      const result = functionToWrap();
      if (result instanceof Promise) {
        refreshPromise = result;
        cachedData = await result;
      } else {
        await wait(0); // Ensure async behavior
        cachedData = result;
      }
      freshUntil = new Date(now.getTime() + freshDurationMs);
      cachedUntil = new Date(now.getTime() + totalCacheMs);
    } catch (e) {
      refreshError = e;
      // Swallow errors during revalidation to allow stale data usage
    } finally {
      refreshPromise = null;
    }
  }

  return async (now: Date = new Date()) => {
    if (cachedData && freshUntil instanceof Date && now <= freshUntil) {
      return cachedData;
    }
    if (cachedData && cachedUntil instanceof Date && now <= cachedUntil) {
      refreshCache(now);
      return cachedData;
    }
    cachedData = null;
    await refreshCache(now);
    if (cachedData === null) {
      throw refreshError || new Error("Unknown error during cache refresh");
    }
    return cachedData;
  };
}
